import { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle,
  WarningCircle,
  XCircle,
  ArrowsCounterClockwise,
  Gear,
  Lock,
  Pencil,
  CopySimple,
  Info,
  TrashSimple,
  Sparkle,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

// ─── Status helpers ────────────────────────────────────────────────────────
const STATUS_STYLES = {
  ok:      { color: "#0F7A1F", bg: "#E8F5E9", border: "#A5D6A7", label: "Listo",       icon: CheckCircle },
  warning: { color: "#B45309", bg: "#FFF8E1", border: "#FFE0B2", label: "Atención",    icon: WarningCircle },
  missing: { color: "#B71C1C", bg: "#FFEBEE", border: "#FFCDD2", label: "Falta",       icon: XCircle },
  optional:{ color: "#71717A", bg: "#F4F4F5", border: "#E4E4E7", label: "Opcional",    icon: Info },
};

function Pill({ status, children }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.optional;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-1 border"
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
      data-testid={`pill-${status}`}
    >
      <Icon size={12} weight="fill" /> {children || cfg.label}
    </span>
  );
}

function OverallBanner({ overall, missingEnv, missingDb }) {
  if (overall === "ready") {
    return (
      <div
        className="mb-6 px-5 py-4 border bg-[#E8F5E9] border-[#A5D6A7] flex items-center gap-3"
        data-testid="setup-overall-ready"
      >
        <ShieldCheck size={28} weight="duotone" className="text-[#0F7A1F]" />
        <div>
          <div className="text-sm font-bold text-[#0F7A1F]">Todo configurado</div>
          <div className="text-xs text-zinc-700">
            La app está lista para operar. Puedes seguir afinando o iniciar reset abajo.
          </div>
        </div>
      </div>
    );
  }
  if (overall === "critical") {
    return (
      <div
        className="mb-6 px-5 py-4 border bg-[#FFEBEE] border-[#FFCDD2] flex items-start gap-3"
        data-testid="setup-overall-critical"
      >
        <XCircle size={28} weight="fill" className="text-[#B71C1C] shrink-0" />
        <div>
          <div className="text-sm font-bold text-[#B71C1C]">
            Hay {missingEnv} variable(s) críticas del servidor sin configurar
          </div>
          <div className="text-xs text-zinc-700">
            Revisa la sección «Variables del servidor» — necesitas editarlas en el Panel Deploy de Emergent y re-deployar.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="mb-6 px-5 py-4 border bg-[#FFF8E1] border-[#FFE0B2] flex items-start gap-3"
      data-testid="setup-overall-warning"
    >
      <WarningCircle size={28} weight="fill" className="text-[#B45309] shrink-0" />
      <div>
        <div className="text-sm font-bold text-[#B45309]">
          Faltan {missingDb} datos del negocio
        </div>
        <div className="text-xs text-zinc-700">
          Llena los campos marcados en rojo abajo para activar todas las funciones (WhatsApp, transferencias, alertas, etc.).
        </div>
      </div>
    </div>
  );
}

// ─── Server-side env-var card ──────────────────────────────────────────────
function EnvVarCard({ entry, onShowHelp }) {
  return (
    <div
      className="border border-zinc-200 bg-white p-4 flex flex-col gap-2"
      data-testid={`env-card-${entry.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-ink truncate">{entry.label}</span>
            <Pill status={entry.status} />
            {entry.test_mode && (
              <span className="text-[10px] uppercase tracking-wide font-bold text-[#B45309]">
                modo prueba
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-1 leading-snug">{entry.description}</p>
        </div>
        <Lock size={16} weight="duotone" className="text-zinc-400 shrink-0" title="Read-only desde la app" />
      </div>
      <div className="flex items-center justify-between gap-3 mt-1 pt-2 border-t border-zinc-100">
        <code className="text-[11px] font-mono text-zinc-500 truncate" data-testid={`env-mask-${entry.key}`}>
          {entry.masked || <span className="text-[#B71C1C]">(vacío)</span>}
        </code>
        <button
          type="button"
          onClick={() => onShowHelp(entry)}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wide font-semibold px-2 py-1 border border-klein text-klein hover:bg-klein/5"
          data-testid={`env-howto-${entry.key}`}
        >
          <Info size={11} weight="bold" /> Cómo cambiarla
        </button>
      </div>
    </div>
  );
}

function EnvHowToModal({ entry, onClose }) {
  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Copiado");
  };
  if (!entry) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-lg w-full p-6 border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="env-howto-modal"
      >
        <div className="flex items-center gap-2 mb-3">
          <Lock size={20} weight="duotone" className="text-klein" />
          <h3 className="font-bold text-lg">{entry.label}</h3>
          <Pill status={entry.status} />
        </div>
        <p className="text-sm text-zinc-700 mb-4">{entry.description}</p>
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-500 mb-1">Dónde se cambia</div>
            <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-zinc-800">{entry.where}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-500 mb-1">Cómo se consigue</div>
            <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-zinc-800">{entry.how_to_get}</div>
          </div>
          <div className="bg-[#FFF8E1] border-l-4 border-[#B45309] px-3 py-2 text-xs text-[#5D4037]">
            <strong>Nota:</strong> No se puede editar desde la app — es una variable del servidor.
            Cámbiala en el Panel Deploy de Emergent y haz <strong>Redeploy</strong>.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={() => copy(entry.label)}
            className="px-3 py-1.5 text-xs border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-1"
          >
            <CopySimple size={11} weight="bold" /> Copiar nombre
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs bg-klein text-white hover:bg-klein-dark"
            data-testid="env-howto-close"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DB setting card (editable) ────────────────────────────────────────────
function DbFieldCard({ field, onEdit }) {
  const display = useMemo(() => {
    if (field.kind === "toggle") return field.value ? "Sí" : "No";
    if (field.kind === "number") return String(field.value ?? 0);
    return field.value || <span className="text-[#B71C1C]">(vacío)</span>;
  }, [field]);

  return (
    <div
      className="border border-zinc-200 bg-white p-4 flex flex-col gap-2"
      data-testid={`db-card-${field.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-ink">{field.label}</span>
            <Pill status={field.status} />
            {field.required && (
              <span className="text-[10px] uppercase tracking-wide font-semibold text-[#B71C1C]">
                requerido
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-1 leading-snug">{field.help}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 mt-1 pt-2 border-t border-zinc-100">
        <span className="text-[11px] text-zinc-700 truncate flex-1">
          {display}
        </span>
        <button
          type="button"
          onClick={() => onEdit(field)}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wide font-semibold px-2 py-1 bg-klein text-white hover:bg-klein-dark"
          data-testid={`db-edit-${field.key}`}
        >
          <Pencil size={11} weight="bold" /> Editar
        </button>
      </div>
    </div>
  );
}

function DbEditModal({ field, onClose, onSaved }) {
  const initial = useMemo(() => {
    if (!field) return "";
    if (field.sensitive) return "";
    if (field.kind === "toggle") return field.value ? "true" : "false";
    return field.value ?? "";
  }, [field]);
  const [val, setVal] = useState(initial);
  const [busy, setBusy] = useState(false);

  if (!field) return null;

  const save = async () => {
    setBusy(true);
    try {
      let outVal = val;
      if (field.kind === "toggle") outVal = val === "true" || val === true;
      if (field.kind === "number") outVal = Number(val) || 0;
      const { data } = await api.post("/admin/setup/db-setting", {
        key: field.key,
        value: outVal,
      });
      toast.success(`${field.label} actualizado`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-md w-full p-6 border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="db-edit-modal"
      >
        <div className="flex items-center gap-2 mb-2">
          <Pencil size={18} weight="duotone" className="text-klein" />
          <h3 className="font-bold text-lg">{field.label}</h3>
        </div>
        <p className="text-xs text-zinc-600 mb-4">{field.help}</p>

        {field.kind === "toggle" ? (
          <div className="flex items-center gap-3 mb-4">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={val === "true" || val === true}
                onChange={(e) => setVal(e.target.checked ? "true" : "false")}
                data-testid="db-edit-toggle"
                className="w-5 h-5 accent-klein"
              />
              <span className="text-sm">Activado</span>
            </label>
          </div>
        ) : field.kind === "select" ? (
          <select
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none mb-4"
            data-testid="db-edit-select"
          >
            {(field.options || []).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : field.kind === "number" ? (
          <input
            type="number"
            min={field.min}
            max={field.max}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none mb-4"
            data-testid="db-edit-number"
            autoFocus
          />
        ) : (
          <input
            type={field.kind === "password" ? "password" : "text"}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={field.sensitive ? "Pega el nuevo valor (lo guardamos cifrado)" : ""}
            className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none mb-4 font-mono"
            data-testid="db-edit-input"
            autoFocus
          />
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-4 py-2 text-sm font-semibold bg-klein text-white hover:bg-klein-dark disabled:opacity-50"
            data-testid="db-edit-save"
          >
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Telegram resync utility ───────────────────────────────────────────────
function TelegramResync() {
  const [busy, setBusy] = useState(false);
  const resync = async () => {
    if (!window.confirm("Esto descarta TODOS los mensajes de Telegram aún sin procesar (comandos /r, /v, etc. en cola) y avanza el cursor al último update. Úsalo si el bot está repitiendo respuestas en bucle. ¿Continuar?")) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/setup/telegram-resync", {});
      toast.success(`Cola de Telegram drenada · nuevo cursor ${data.new_cursor}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo resincronizar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className="mt-6 border border-amber-300 bg-amber-50/60 p-5"
      data-testid="setup-telegram-resync"
    >
      <div className="flex items-start gap-3">
        <ArrowsCounterClockwise size={24} weight="duotone" className="text-amber-700 shrink-0" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-amber-900">Resincronizar Telegram</h2>
          <p className="text-xs text-zinc-700 mt-1 leading-snug">
            ¿El bot te está mandando el mismo mensaje en bucle cada 30 segundos? Esto
            limpia la cola pendiente y avanza el cursor al último update real, así
            el bot deja de repetir comandos viejos. Se ejecuta al instante.
          </p>
          <button
            type="button"
            onClick={resync}
            disabled={busy}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 disabled:opacity-50"
            data-testid="telegram-resync-btn"
          >
            <ArrowsCounterClockwise size={12} weight="bold" />
            {busy ? "Drenando…" : "Drenar cola y resincronizar"}
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Reset zone ────────────────────────────────────────────────────────────
function ResetZone({ counts, onAfterReset }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [keepSuc, setKeepSuc] = useState(true);
  const [keepBiz, setKeepBiz] = useState(true);
  const [busy, setBusy] = useState(false);
  const total = useMemo(
    () => Object.values(counts || {}).reduce((a, b) => a + (b || 0), 0),
    [counts],
  );

  const submit = async () => {
    if (confirm !== "RESET") {
      toast.error("Escribe RESET (mayúsculas) para confirmar");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/setup/reset", {
        confirm: "RESET",
        keep_sucursales: keepSuc,
        keep_business_settings: keepBiz,
      });
      const wiped = Object.entries(data.deleted || {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ") || "0";
      toast.success(`Reset completo. Borrados — ${wiped}`);
      setOpen(false);
      setConfirm("");
      onAfterReset?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reiniciar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-10 border-2 border-[#B71C1C] bg-[#FFEBEE]/40 p-5"
      data-testid="setup-reset-zone"
    >
      <div className="flex items-start gap-3">
        <TrashSimple size={26} weight="duotone" className="text-[#B71C1C] shrink-0" />
        <div className="flex-1">
          <h2 className="text-lg font-bold text-[#B71C1C]">Zona peligrosa · Reset</h2>
          <p className="text-sm text-zinc-700 mt-1">
            Borra todos los registros operativos para arrancar la app en cero.
            Conserva tu usuario admin (no te quedas fuera).
          </p>
          <p className="text-xs text-zinc-600 mt-2">
            Datos actuales: <strong>{counts.products}</strong> productos · <strong>{counts.sales}</strong> ventas · <strong>{counts.conversations}</strong> charlas · <strong>{counts.posts}</strong> publicaciones · <strong>{counts.subscribers}</strong> suscriptores · <strong>{counts.audit_log}</strong> registros de auditoría · <strong>{total}</strong> documentos totales.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#B71C1C] text-white text-sm font-bold hover:bg-[#8B0000]"
            data-testid="reset-open"
          >
            <TrashSimple size={14} weight="bold" /> Iniciar reset
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div
            className="bg-white max-w-lg w-full p-6 border-2 border-[#B71C1C]"
            data-testid="reset-modal"
          >
            <div className="flex items-center gap-2 mb-3">
              <WarningCircle size={24} weight="fill" className="text-[#B71C1C]" />
              <h3 className="font-bold text-lg text-[#B71C1C]">¿Reiniciar la plataforma?</h3>
            </div>
            <p className="text-sm text-zinc-700 mb-3">
              Esta acción es <strong>permanente</strong>. Borra productos, ventas, charlas, publicaciones,
              suscriptores, referidos, auditoría y otros usuarios cajeros/empleados.
            </p>
            <div className="space-y-2 mb-4">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepBiz}
                  onChange={(e) => setKeepBiz(e.target.checked)}
                  className="mt-1 accent-klein"
                  data-testid="reset-keep-biz"
                />
                <span>
                  <strong>Conservar configuración del negocio</strong>
                  <span className="block text-xs text-zinc-600">
                    Mantiene Telegram, banco, redes, WhatsApp, etc. Recomendado.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepSuc}
                  onChange={(e) => setKeepSuc(e.target.checked)}
                  className="mt-1 accent-klein"
                  data-testid="reset-keep-suc"
                />
                <span>
                  <strong>Conservar sucursales</strong>
                  <span className="block text-xs text-zinc-600">
                    Si quieres reusar tus sucursales actuales, déjalo activado.
                  </span>
                </span>
              </label>
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold text-[#B71C1C] block mb-1">
                Escribe RESET para confirmar
              </label>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="RESET"
                className="w-full px-3 py-2 text-sm border-2 border-[#B71C1C] focus:outline-none font-mono uppercase"
                data-testid="reset-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setOpen(false); setConfirm(""); }}
                disabled={busy}
                className="px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="reset-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || confirm !== "RESET"}
                className="px-4 py-2 text-sm font-bold bg-[#B71C1C] text-white hover:bg-[#8B0000] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-confirm"
              >
                {busy ? "Borrando…" : "Sí, borrar todo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function Setup() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpEntry, setHelpEntry] = useState(null);
  const [editField, setEditField] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/setup/status");
      setData(data);
    } catch (e) {
      toast.error("No se pudo cargar el wizard");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once when user becomes available. The fetch is wrapped in a guard so
  // it runs at most once per role transition without needing eslint overrides.
  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await api.get("/admin/setup/status");
        if (!cancelled) setData(payload);
      } catch {
        if (!cancelled) toast.error("No se pudo cargar el wizard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Non-admins get redirected via <Navigate> instead of an imperative
  // navigate() inside useEffect (which trips React 19's set-state-in-effect rule).
  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  // Group DB fields by their `group` for sectioned rendering. Cheap O(n)
  // computation on every render — N is ~25 so memoizing isn't worth the
  // extra hook dependency cost.
  const grouped = (() => {
    if (!data?.db_fields) return {};
    const out = {};
    for (const f of data.db_fields) {
      const g = f.group || "Otros";
      if (!out[g]) out[g] = [];
      out[g].push(f);
    }
    return out;
  })();

  if (loading || !data) {
    return (
      <div className="px-6 py-12 text-center text-sm text-zinc-500" data-testid="setup-loading">
        Cargando wizard…
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-6xl mx-auto" data-testid="setup-page">
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Sparkle size={28} weight="duotone" className="text-klein" />
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Instalación
          </h1>
        </div>
        <p className="text-sm text-zinc-600 max-w-2xl">
          Un solo lugar para configurar todo lo que la plataforma necesita para operar.
          Edita con un click; los cambios se aplican al instante. Cuando termines, puedes hacer
          reset y arrancar limpio.
        </p>
      </header>

      <OverallBanner
        overall={data.overall}
        missingEnv={data.critical_missing_count}
        missingDb={data.required_db_missing_count}
      />

      {/* SECTION 1: Server-side env vars */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={18} weight="duotone" className="text-zinc-700" />
          <h2 className="text-xl font-bold">Variables del servidor</h2>
          <span className="text-[11px] text-zinc-500 ml-2">Read-only · se editan en el Panel Deploy</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.env_vars.map((e) => (
            <EnvVarCard key={e.key} entry={e} onShowHelp={setHelpEntry} />
          ))}
        </div>
      </section>

      {/* SECTION 2: DB settings — editable */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Gear size={18} weight="duotone" className="text-klein" />
          <h2 className="text-xl font-bold">Datos del negocio</h2>
          <span className="text-[11px] text-zinc-500 ml-2">Editables al instante</span>
        </div>
        {Object.entries(grouped).map(([group, fields]) => (
          <div key={group} className="mb-5">
            <h3 className="text-[11px] uppercase tracking-wide font-bold text-zinc-500 mb-2">{group}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map((f) => (
                <DbFieldCard key={f.key} field={f} onEdit={setEditField} />
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* SECTION 3: Reset */}
      <ResetZone counts={data.counts} onAfterReset={load} />

      {/* Telegram resync utility */}
      <TelegramResync />

      {/* Refresh button */}
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-zinc-300 hover:bg-zinc-50"
          data-testid="setup-refresh"
        >
          <ArrowsCounterClockwise size={12} weight="bold" /> Refrescar estado
        </button>
      </div>

      <EnvHowToModal entry={helpEntry} onClose={() => setHelpEntry(null)} />
      <DbEditModal
        field={editField}
        onClose={() => setEditField(null)}
        onSaved={() => { setEditField(null); load(); }}
      />
    </div>
  );
}
